// Per-(date × brandAccount × origin × model) AI token usage row, as fetched
// from the analyzer / overall subgraphs.
export type AiUsageBucket = {
  brandAccountId?: string | null
  cachedTokens: number
  calls: number
  date: string
  model?: string | null
  origin?: string | null
  outputTokens: number
  promptTokens: number
  thoughtsTokens: number
}

const csvColumns = [
  'date',
  'brandAccountId',
  'brandAccountName',
  'feature',
  'model',
  'calls',
  'promptTokens',
  'outputTokens',
  'cachedTokens',
  'thoughtsTokens',
] as const

// Returns the current month as `YYYY-MM`.
export const currentMonth = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')

  return `${year}-${month}`
}

// Identity key for a bucket. The analyzer and overall lists are disjoint by
// (model, origin), so the full tuple uniquely identifies a row across both.
export const bucketKey = (bucket: AiUsageBucket): string =>
  [
    bucket.date,
    bucket.brandAccountId ?? '',
    bucket.origin ?? '',
    bucket.model ?? '',
  ].join('\u0000')

// Merges buckets from both subgraphs, summing any that share a key. The two
// lists are normally disjoint (distinct models per subgraph), so this is
// effectively concat + sort; if they ever overlap, summing yields the correct
// per-cell total rather than crashing the whole export. Sorted by
// date → brandAccountId → origin → model (`date` is `YYYY-MM-DD`, so lexical
// order is chronological).
export const mergeBuckets = (buckets: AiUsageBucket[]): AiUsageBucket[] => {
  const byKey = new Map<string, AiUsageBucket>()

  buckets.forEach((bucket) => {
    const key = bucketKey(bucket)
    const existing = byKey.get(key)

    if (existing) {
      existing.calls += bucket.calls
      existing.promptTokens += bucket.promptTokens
      existing.outputTokens += bucket.outputTokens
      existing.cachedTokens += bucket.cachedTokens
      existing.thoughtsTokens += bucket.thoughtsTokens
    } else {
      byKey.set(key, { ...bucket })
    }
  })

  return [...byKey.values()].sort((a, b) =>
    bucketKey(a).localeCompare(bucketKey(b)),
  )
}

// Escapes a single CSV field. First neutralizes CSV formula injection (OWASP) by
// prefixing a single quote when the field — after skipping any leading whitespace —
// starts with a formula trigger (`=`, `+`, `-`, `@`) or a leading tab/CR (which a
// spreadsheet may strip to expose a formula), so it is treated as text. Leading
// whitespace alone must not bypass the guard. Then quotes the value (doubling inner
// quotes) when it contains a comma, quote, or newline — brand names may contain commas.
export const csvEscape = (value: string): string => {
  const guarded = /^[\s]*[=+\-@\t\r]/.test(value) ? `'${value}` : value

  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded
}

// Renders merged buckets as CSV text, resolving brand names from the account
// map. A null brand yields empty id/name but keeps the row (real spend).
export const toCsv = (
  buckets: AiUsageBucket[],
  brandAccountNameById: Map<string, string>,
): string => {
  const header = csvColumns.join(',')
  const rows = buckets.map((bucket) => {
    const brandAccountId = bucket.brandAccountId ?? ''
    const brandAccountName = brandAccountNameById.get(brandAccountId) ?? ''

    return [
      bucket.date,
      brandAccountId,
      brandAccountName,
      bucket.origin ?? '',
      bucket.model ?? '',
      bucket.calls,
      bucket.promptTokens,
      bucket.outputTokens,
      bucket.cachedTokens,
      bucket.thoughtsTokens,
    ]
      .map((field) => csvEscape(`${field}`))
      .join(',')
  })

  return [header, ...rows].join('\n')
}

// Triggers a client-side download of `csv` as `filename`. Prepends a UTF-8 BOM
// so Excel reads Japanese brand names correctly.
export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([`\uFEFF${csv}`], {
    type: 'text/csv;charset=utf-8',
  })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
